import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { SearchHistoryRecord } from '../../../../services/analyticsService';
import { format } from 'date-fns';

interface SearchAnalyticsProps {
    data: SearchHistoryRecord[];
    dailyData: Array<{ date: string; searches: number }>;
}

const SearchAnalytics: React.FC<SearchAnalyticsProps> = ({ data, dailyData }) => {
    const statusCounts = {
        success: data.filter(s => s.status === 'success').length,
        error: data.filter(s => s.status === 'error').length,
        no_results: data.filter(s => s.status === 'no_results').length,
    };

    const chartData = dailyData.map(d => ({
        date: format(new Date(d.date), 'dd/MM'),
        Pesquisas: d.searches
    }));

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                📊 Análise de Pesquisas
            </h3>

            {/* Status Summary */}
            <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Sucesso</p>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">{statusCounts.success}</p>
                </div>
                <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Erro</p>
                    <p className="text-2xl font-bold text-red-600 dark:text-red-400">{statusCounts.error}</p>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Sem Resultados</p>
                    <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{statusCounts.no_results}</p>
                </div>
            </div>

            {/* Chart */}
            {chartData.length > 0 ? (
                <div className="mb-6">
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                            <XAxis dataKey="date" stroke="#9CA3AF" />
                            <YAxis stroke="#9CA3AF" />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: '#1F2937',
                                    border: '1px solid #374151',
                                    borderRadius: '8px'
                                }}
                            />
                            <Legend />
                            <Line type="monotone" dataKey="Pesquisas" stroke="#3B82F6" strokeWidth={2} dot={{ fill: '#3B82F6' }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            ) : (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    Nenhum dado disponível para o período selecionado
                </div>
            )}

            {/* Recent Searches Table */}
            <div className="overflow-x-auto">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Pesquisas Recentes</h4>
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Query
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Resultados
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Duração
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Data
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {data.slice(0, 10).map((search) => (
                            <tr key={search.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                    {search.search_query}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${search.status === 'success' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                                            search.status === 'error' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' :
                                                'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                                        }`}>
                                        {search.status}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                    {search.results_count}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                    {(search.search_duration_ms / 1000).toFixed(2)}s
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                                    {format(new Date(search.created_at), 'dd/MM/yyyy HH:mm')}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {data.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        Nenhuma pesquisa encontrada
                    </div>
                )}
            </div>
        </div>
    );
};

export default SearchAnalytics;
